import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import {
  ChevronRightIcon,
  EllipsisIcon,
} from "lucide-react";

import { cn } from "../util.cn";

type BreadcrumbContextType = {
  size: "1" | "2";
};

const BreadcrumbContext = React.createContext<BreadcrumbContextType | null>(null);

const useBreadcrumbContext = () => {
  const context = React.useContext(BreadcrumbContext);

  if (!context) {
    throw new Error("useBreadcrumbContext must be used within a BreadcrumbRoot");
  }

  return context;
};

interface RootProps extends BreadcrumbContextType, React.ComponentPropsWithoutRef<"nav"> {
  separator?: React.ReactNode;
}
const Root = React.forwardRef<
  HTMLElement,
  RootProps
>((props, ref) => {
  const { size = "2" } = props;
  return (
    <BreadcrumbContext.Provider value={{ size }}>
      <nav aria-label="breadcrumb" ref={ref} {...props} />
    </BreadcrumbContext.Provider>
  );
});
Root.displayName = "Breadcrumb";

const List = React.forwardRef<
  HTMLOListElement,
  React.ComponentPropsWithoutRef<"ol">
>(({ className, ...props }, ref) => {
  const { size } = useBreadcrumbContext();

  return (
    <ol
      className={cn(
      `
        flex flex-wrap items-center break-words text-gray-11
      `,
      size === "2" && `
        gap-1.5 text-2
        sm:gap-2.5
      `,
      size === "1" && `
        gap-1 text-1
        sm:gap-2
      `,
      className,
      )}
      ref={ref}
      {...props}
    />
  );
});
List.displayName = "BreadcrumbList";

const Item = React.forwardRef<
  HTMLLIElement,
  React.ComponentPropsWithoutRef<"li">
>(({ className, ...props }, ref) => {
  const { size } = useBreadcrumbContext();

  return (
    <li
      className={cn(
        size === "2" && `gap-1.5`,
        size === "1" && `gap-1`,
        className,
      )}
      ref={ref}
      {...props}
    />
  );
});
Item.displayName = "BreadcrumbItem";

const Link = React.forwardRef<
  HTMLAnchorElement,
  {
    asChild?: boolean;
  } & React.ComponentPropsWithoutRef<"a">
>(({ asChild, className, ...props }, ref) => {
  const Comp = asChild ? Slot : "a";

  return (
    <Comp
      className={cn(`
        transition-colors
        hover:text-gray-12
      `, className)}
      ref={ref}
      {...props}
    />
  );
});
Link.displayName = "BreadcrumbLink";

const Page = React.forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<"span">
>(({ className, ...props }, ref) => {
  return (
    <span
      aria-current="page"
      aria-disabled="true"
      className={cn("text-gray-11", className)}
      ref={ref}
      role="link"
      {...props}
    />
  );
});
Page.displayName = "BreadcrumbPage";

const Separator = ({
  children,
  className,
  ...props
}: React.ComponentProps<"li">) => {
  return (
    <li
      aria-hidden="true"
      className={cn(`
        mt-0.5
        [&>svg]:size-4
      `, className)}
      role="presentation"
      {...props}
    >
      {children ?? <ChevronRightIcon />}
    </li>
  );
};
Separator.displayName = "BreadcrumbSeparator";

const Ellipsis = ({
  className,
  ...props
}: React.ComponentProps<"span">) => {
  const { size } = useBreadcrumbContext();

  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex items-center justify-center",
        size === "2" && "size-9",
        size === "1" && "size-6",
        className,
      )}
      role="presentation"
      {...props}
    >
      <EllipsisIcon className="size-4" />
      <span className="sr-only">More</span>
    </span>
  );
};
Ellipsis.displayName = "BreadcrumbElipssis";

export {
  Ellipsis,
  Item,
  Link,
  List,
  Page,
  Root,
  Separator,
};
